import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import * as XLSX from 'xlsx';
import { staffService } from '../../services/firestore';
import { sendEmail } from '../../utils/emailService';
import RequirePermission from '../../components/RequirePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { usePermissions } from '../../hooks/usePermissions';

// Unified UI Imports
import logger from '../../utils/logger';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../ui/Table/Table';
import Button from '../../ui/Button';
import IconButton from '../../ui/IconButton';
import Card from '../../ui/Card';
import Icon from '../../ui/Icon';
import { CellText, CellId, CellNumber, CellDate, CellStatus, CellTag } from '../../ui/DataTable/cells';
import EmptyState from '../../ui/EmptyState';
import LoadingSpinner from '../../components/LoadingSpinner';
import Stat, { StatGrid } from '../../ui/Stat/Stat';
import { Input, Checkbox } from '../../ui/Field/Field';
import { confirmDialog, toast } from '../../ui/feedback';
import { ICONS } from '../../ui/icon-map';
import { useSearchLogger } from '../../hooks/useSearchLogger';
import { staffActionsFor } from './staffRules';

// Summary tile (KPI) that also filters the table
const StaffSummaryCard = ({ title, count, tone, active, onClick, icon }) => (
    <Stat
        label={title}
        value={count}
        icon={icon}
        tone={tone}
        onClick={onClick}
        className={active ? 'ring-2 ring-purple-500' : ''}
        title={active ? `${title} (selected)` : title}
    />
);

function Staff() {
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    // Editing and deleting write the staff records, which the database rules allow only with write_staff or
    // import_staff: the page's own edit_staff / delete_staff alone showed buttons that always failed (ADM-02).
    const { edit: canEditStaff, remove: canDeleteStaff } = staffActionsFor(hasPermission);
    const [employees, setEmployees] = useState([]);
    const [filteredEmployees, setFilteredEmployees] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [view, setView] = useState('all');
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [syncing, setSyncing] = useState(false);
    const [summary, setSummary] = useState({ total: 0, expiring: 0, expired: 0 });
    const [editedRows, setEditedRows] = useState({});


    // Logger
    const logSearch = useSearchLogger('STAFF');

    // UI States
    const [activeMenu, setActiveMenu] = useState(null);

    const [editingCell, setEditingCell] = useState(null);
    const [visibleColumns, setVisibleColumns] = useState({
        staffNo: true,
        name: true,
        job: true,
        nationality: true,
        cardExpiry: true,
        daysLeft: true,
        status: true,
        passportNumber: false,
        cardType: false,
        cardNumber: false,
        passportIssueDate: false,
        passportExpireDate: false,
        email: false,
        joiningDate: false,
        years: false
    });

    const formatName = (name) => {
        if (!name) return '';
        return name.toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
    };

    // Messages go to the app's shared toast stack (the old local list was never rendered)
    const showToast = (message, type = 'success') => toast(message, type);

    // Editing Logic
    const handleFieldChange = (staffNo, field, value) => {
        setEditedRows(prev => ({
            ...prev,
            [staffNo]: { ...prev[staffNo], [field]: value }
        }));
    };

    const handleCellDoubleClick = (staffNo, field) => {
        if (canEditStaff) {
            setEditingCell({ staffNo, field });
        } else {
            logger.warn("No permission to edit.");
        }
    };
    const handleCellBlur = () => setEditingCell(null);

    const toggleColumn = (columnKey) => {
        setVisibleColumns(prev => ({ ...prev, [columnKey]: !prev[columnKey] }));
    };

    const columnLabels = {
        staffNo: 'Staff No',
        name: 'Name',
        job: 'Job',
        nationality: 'Nationality',
        cardExpiry: 'Expiry Date',
        daysLeft: 'Days Left',
        status: 'Status',
        passportNumber: 'Passport Number',
        cardType: 'Card Type',
        cardNumber: 'Card Number',
        passportIssueDate: 'Passport Issue Date',
        passportExpireDate: 'Passport Expire Date',
        email: 'Email',
        joiningDate: 'Joining Date',
        years: 'Years'
    };

    const handleDateChange = (staffNo, dateString) => {
        const parts = dateString.split('/');
        if (parts.length === 3) {
            const day = parseInt(parts[0]);
            const month = parseInt(parts[1]) - 1;
            const year = parseInt(parts[2]);
            const newDate = new Date(year, month, day);
            if (!isNaN(newDate)) {
                handleFieldChange(staffNo, 'cardExpiry', newDate);
            }
        }
    };

    const formatAndSetDate = (staffNo, inputElement) => {
        const value = inputElement.value.trim();
        if (!value) return;
        const parts = value.split('/');
        if (parts.length === 3) {
            const day = parts[0].padStart(2, '0');
            const month = parts[1].padStart(2, '0');
            const year = parts[2];
            const formatted = `${day}/${month}/${year}`;
            inputElement.value = formatted;
            handleDateChange(staffNo, formatted);
        }
    };

    const updateSummary = useCallback((data) => {
        const total = data.length;
        const expiring = data.filter(emp => emp.daysUntilExpiry != null && emp.daysUntilExpiry <= 30 && emp.daysUntilExpiry > 0).length;
        const expired = data.filter(emp => emp.daysUntilExpiry != null && emp.daysUntilExpiry <= 0).length;
        setSummary({ total, expiring, expired });
    }, []);

    const fetchFromDB = useCallback(async () => {
        setLoading(true);
        try {
            // the staff store only (owner, 18 Oct 2026: no Google Sheet fallback - it held passport data publicly)
            const dbData = await staffService.getAllStaff();
            setEmployees(dbData);
            updateSummary(dbData);
            setLoadError('');
        } catch (error) {
            logger.error(error);
            const msg = String(error?.message || '');
            setLoadError(/permission|false for '/i.test(msg) ? 'You do not have access to the staff list, or the server refused the read.'
                : (msg || 'The staff list could not be read'));
        } finally {
            setLoading(false);
        }
    }, [updateSummary]);

    const handleExport = () => {
        const dataToExport = getEmployeesToShow().map(emp => ({
            "Staff No": emp.staffNo,
            "Name": emp.name,
            "Job": emp.job,
            "Nationality": emp.nationality,
            "Passport Number": emp.passportNumber,
            "Card Type": emp.cardType,
            "Card Number": emp.cardNumber,
            "Card Expiry": emp.cardExpiry instanceof Date ? emp.cardExpiry.toLocaleDateString('en-GB') : '',
            "Passport Issue Date": emp.passportIssueDate,
            "Passport Expire Date": emp.passportExpireDate,
            "Email": emp.email,
            "Joining Date": emp.joiningDate,
            "Years of Service": emp.years,
            "Days Until Expiry": emp.daysUntilExpiry != null ? emp.daysUntilExpiry : '—',
            "Status": getStatusText(emp.daysUntilExpiry)
        }));

        const ws = XLSX.utils.json_to_sheet(dataToExport);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Staff_Data");
        XLSX.writeFile(wb, `Staff_Export_${new Date().toISOString().slice(0, 10)}.xlsx`);
        showToast("Export successful!", "success");
    };

    const handleSaveAll = async () => {
        const editedStaffNos = Object.keys(editedRows);
        if (editedStaffNos.length === 0) {
            showToast('No changes to save', 'warning');
            return;
        }
        setSyncing(true);
        try {
            // only the fields that were edited, for only the employees that were edited - nothing else is written
            // (the old save deleted every employee that was not in the edited list)
            const EDITABLE = ['name', 'job', 'nationality', 'cardExpiry'];
            const edits = [];
            const updatesToSave = [];
            for (const staffNo of editedStaffNos) {
                const employee = employees.find(emp => emp.staffNo === staffNo);
                if (!employee) continue;
                const fields = Object.fromEntries(EDITABLE.filter((k) => editedRows[staffNo][k] !== undefined).map((k) => [k, editedRows[staffNo][k]]));
                if (!Object.keys(fields).length) continue;
                edits.push({ staffNo: employee.staffNo, fields });
                const updatedData = { staffNo: employee.staffNo, ...fields };
                const expiry = 'cardExpiry' in fields ? fields.cardExpiry : employee.cardExpiry;
                const expiryDate = expiry instanceof Date ? expiry : (expiry ? new Date(expiry) : null);
                updatedData.daysUntilExpiry = expiryDate && !isNaN(expiryDate)
                    ? Math.ceil((expiryDate.getTime() - Date.now()) / (1000 * 3600 * 24)) : null;
                updatesToSave.push(updatedData);
            }
            await staffService.saveStaffEdits(edits);
            let updatedEmployees = [...employees];
            updatesToSave.forEach(updated => {
                updatedEmployees = updatedEmployees.map(emp =>
                    emp.staffNo === updated.staffNo ? { ...emp, ...updated } : emp
                );
            });
            setEmployees(updatedEmployees);
            updateSummary(updatedEmployees);
            setEditedRows({});
            showToast(`Successfully saved ${updatesToSave.length} record(s)!`, 'success');
        } catch (error) {
            logger.error('Error saving:', error);
            showToast('Save failed!', 'error');
        } finally {
            setSyncing(false);
        }
    };

    const handleDeleteEmployee = async (staffNo) => {
        if (!(await confirmDialog({ title: 'Delete this employee?', message: 'The employee record is deleted for good.', confirmLabel: 'Delete', danger: true }))) return;
        
        try {
            const normalizedNo = staffNo?.toString().trim();
            await staffService.deleteStaff(normalizedNo);
            
            // Type-safe and trim-safe filter
            const updatedEmployees = employees.filter(emp => {
                const empNo = emp.staffNo?.toString().trim();
                return empNo !== normalizedNo;
            });
            
            setEmployees(updatedEmployees);
            updateSummary(updatedEmployees);
            showToast('Employee deleted', 'success');
        } catch (error) {
            logger.error('Error deleting employee:', error);
            showToast('Could not delete the employee', 'error');
        }
    };

    const handleSearch = (keyword) => {
        const searchKeyword = keyword.trim().toLowerCase();
        setSearchTerm(keyword);
        if (!searchKeyword) {
            setFilteredEmployees([]);
            setView('all');
            return;
        }
        logSearch(searchKeyword);
        const results = employees.filter(emp =>
            emp.name?.toLowerCase().includes(searchKeyword) ||
            emp.staffNo?.toLowerCase().includes(searchKeyword) ||
            emp.job?.toLowerCase().includes(searchKeyword)
        );
        setFilteredEmployees(results);
        setView('search');
    };

    const getEmployeesToShow = () => {
        if (view === 'search' && searchTerm) return filteredEmployees;
        if (view === 'expiring') return employees.filter(emp => emp.daysUntilExpiry != null && emp.daysUntilExpiry <= 30 && emp.daysUntilExpiry > 0);
        if (view === 'expired') return employees.filter(emp => emp.daysUntilExpiry != null && emp.daysUntilExpiry <= 0);
        return employees;
    };

    useEffect(() => {
        fetchFromDB();
    }, [fetchFromDB]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!activeMenu) return;
            
            const menuButtons = document.querySelectorAll('[class*="yelo-outline-glass-btn"]');
            const menuCards = document.querySelectorAll('[class*="absolute top-full"]');
            
            let isClickOnButton = false;
            let isClickOnMenu = false;
            
            menuButtons.forEach(btn => {
                if (btn.contains(event.target)) isClickOnButton = true;
            });
            
            menuCards.forEach(card => {
                if (card.contains(event.target)) isClickOnMenu = true;
            });
            
            if (!isClickOnButton && !isClickOnMenu) {
                setActiveMenu(null);
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [activeMenu]);

    const getStatusText = (days) => {
        if (days === null || days === undefined) return 'Unknown';
        if (days <= 0) return 'Expired';
        if (days <= 7) return 'Critical';
        if (days <= 30) return 'Expiring Soon';
        return 'Valid';
    };

    const employeesToShow = getEmployeesToShow();

    return (
        <Layout title="Staff Management Portal">
            <div className="w-full">

                {/* Controls Toolbar */}
                <Card className="mb-6 relative z-30" style={{ overflow: 'visible' }} padding="sm">
                    <div className="flex flex-wrap items-center justify-between gap-4">

                        {/* Left Side: Search */}
                        <div className="flex-1 min-w-[240px]">
                            <RequirePermission permission={PERMISSIONS.search_staff} fallback={null}>
                                <Input
                                    type="text"
                                    aria-label="Search staff"
                                    placeholder="Search by Name, Staff No, or Job..."
                                    value={searchTerm}
                                    onChange={(e) => handleSearch(e.target.value)}
                                    prefix={<Icon glyph={ICONS.search} size="sm" />}
                                    suffix={searchTerm ? (
                                        <IconButton icon={ICONS.close} label="Clear search" size="xs" onClick={() => handleSearch('')} />
                                    ) : undefined}
                                />
                            </RequirePermission>
                        </div>

                        {/* Right Side: Actions */}
                        <div className="flex flex-wrap items-center gap-2 relative z-50">
                            <div className="relative">
                                <RequirePermission permission={PERMISSIONS.manage_staff_data} fallback={null}>
                                    <Button
                                        variant="secondary"
                                        onClick={() => setActiveMenu(activeMenu === 'data' ? null : 'data')}
                                        className={`yelo-outline-glass-btn ${activeMenu === 'data' ? 'bg-purple-50 border-purple-200' : ''}`}
                                        aria-expanded={activeMenu === 'data'}
                                        aria-haspopup="menu"
                                        trailingIcon={<Icon glyph={ICONS.chevronDown} size="sm" />}
                                    >
                                        Data Ops
                                    </Button>
                                    {activeMenu === 'data' && (
                                        <Card
                                            className="absolute top-full right-0 mt-2 z-50 w-56 shadow-md py-1"
                                            padding="none"
                                            style={{ position: 'absolute', zIndex: 60 }}
                                        >
                                            <Button variant="ghost" size="sm" fullWidth className="justify-start" onClick={() => { navigate('/database/staff'); setActiveMenu(null); }} leadingIcon={<Icon glyph={ICONS.upload} size="sm" />}>
                                                Import staff (Database page)
                                            </Button>
                                        </Card>
                                    )}
                                </RequirePermission>
                            </div>

                            <div className="relative">
                                <RequirePermission permission={PERMISSIONS.share_staff_reports} fallback={null}>
                                    <Button
                                        variant="secondary"
                                        onClick={() => setActiveMenu(activeMenu === 'share' ? null : 'share')}
                                        className={`yelo-outline-glass-btn ${activeMenu === 'share' ? 'bg-purple-50 border-purple-200' : ''}`}
                                        aria-expanded={activeMenu === 'share'}
                                        aria-haspopup="menu"
                                        leadingIcon={<Icon glyph={ICONS.export} size="sm" />}
                                        trailingIcon={<Icon glyph={ICONS.chevronDown} size="sm" />}
                                    >
                                        Share
                                    </Button>
                                    {activeMenu === 'share' && (
                                        <Card
                                            className="absolute top-full right-0 mt-2 z-50 w-64 shadow-md py-1"
                                            padding="none"
                                            style={{ position: 'absolute', zIndex: 60 }}
                                        >
                                            <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                                                Export
                                            </div>
                                            <Button variant="ghost" size="sm" fullWidth className="justify-start" onClick={() => { handleExport(); setActiveMenu(null); }} leadingIcon={<Icon glyph={ICONS.export} size="sm" />}>
                                                Export to Excel
                                            </Button>

                                            <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 border-t border-gray-100 mt-1">
                                                Keys & Email
                                            </div>
                                            <Button variant="ghost" size="sm" fullWidth className="justify-start" onClick={() => { sendEmail('expired', employees, showToast); setActiveMenu(null); }} leadingIcon={<Icon glyph={ICONS.mail} size="sm" />}>
                                                Expired Only
                                            </Button>
                                            <Button variant="ghost" size="sm" fullWidth className="justify-start" onClick={() => { sendEmail('urgent', employees, showToast); setActiveMenu(null); }} leadingIcon={<Icon glyph={ICONS.alert} size="sm" />}>
                                                Urgent Cases
                                            </Button>
                                            <Button variant="ghost" size="sm" fullWidth className="justify-start" onClick={() => { sendEmail('both', employees, showToast); setActiveMenu(null); }} leadingIcon={<span className="flex gap-1"><Icon glyph={ICONS.mail} size="sm" /><Icon glyph={ICONS.alert} size="sm" /></span>}>
                                                Both (Full)
                                            </Button>
                                        </Card>
                                    )}
                                </RequirePermission>
                            </div>

                            <div className="relative">
                                <Button
                                    variant="secondary"
                                    onClick={() => setActiveMenu(activeMenu === 'columns' ? null : 'columns')}
                                    className={`yelo-outline-glass-btn ${activeMenu === 'columns' ? 'bg-purple-50 border-purple-200' : ''}`}
                                    aria-expanded={activeMenu === 'columns'}
                                    leadingIcon={<Icon glyph={ICONS.settings} size="sm" />}
                                    trailingIcon={<Icon glyph={ICONS.chevronDown} size="sm" />}
                                >
                                    View
                                </Button>
                                {activeMenu === 'columns' && (
                                    <Card
                                        className="absolute top-full right-0 mt-2 z-50 w-64 max-h-[60vh] overflow-y-auto shadow-md py-1"
                                        padding="sm"
                                        style={{ position: 'absolute', zIndex: 60 }}
                                    >
                                        <div className="space-y-1">
                                            {Object.entries(columnLabels).map(([key, label]) => (
                                                <Checkbox key={key} label={label} checked={visibleColumns[key]} onChange={() => toggleColumn(key)} className="p-1.5 rounded hover:bg-gray-50" />
                                            ))}
                                        </div>
                                    </Card>
                                )}
                            </div>

                            <div className="h-8 w-px bg-gray-200 mx-1" aria-hidden="true"></div>

                            {canEditStaff && (
                                <Button
                                    variant="primary"
                                    onClick={handleSaveAll}
                                    disabled={syncing || Object.keys(editedRows).length === 0}
                                    isLoading={syncing}
                                    leadingIcon={<Icon glyph={ICONS.save} size="sm" />}
                                >
                                    {syncing ? 'Saving...' : 'Save Changes'}
                                    {Object.keys(editedRows).length > 0 && (
                                        <span className="ml-2 bg-white/20 px-1.5 py-0.5 rounded text-xs">
                                            {Object.keys(editedRows).length}
                                        </span>
                                    )}
                                </Button>
                            )}
                        </div>
                    </div>
                </Card>

                <RequirePermission permission={PERMISSIONS.view_staff_stats} fallback={null}>
                    <StatGrid min={180} className="mb-6">
                        <StaffSummaryCard title="Total Staff" count={summary.total} active={view === 'all'} onClick={() => setView('all')} icon={ICONS.users} />
                        <StaffSummaryCard title="Expiring Soon" count={summary.expiring} tone="warning" active={view === 'expiring'} onClick={() => setView('expiring')} icon={ICONS.alert} />
                        <StaffSummaryCard title="Expired" count={summary.expired} tone="danger" active={view === 'expired'} onClick={() => setView('expired')} icon={ICONS.expired} />
                    </StatGrid>
                </RequirePermission>

                {/* Unified Table (inline editing -> table parts) */}
                {loading && employees.length === 0 ? (
                    <Card><LoadingSpinner message="Loading staff..." /></Card>
                ) : loadError && employees.length === 0 ? (
                    <Card>
                        <EmptyState icon={ICONS.alert} title="The staff list could not be read" description={loadError}
                            action={<Button size="sm" variant="secondary" onClick={fetchFromDB}>Try again</Button>} />
                    </Card>
                ) : employeesToShow.length === 0 ? (
                    <Card><EmptyState icon={ICONS.users} title="No staff found" description={searchTerm ? 'No employees match your search.' : 'No employees in this view.'} /></Card>
                ) : (
                <TableContainer>
                    <Table sticky>
                        <TableHead>
                            <TableRow>
                                <TableHeader>#</TableHeader>
                                {visibleColumns.staffNo && <TableHeader>Staff No</TableHeader>}
                                {visibleColumns.name && <TableHeader>Name</TableHeader>}
                                {visibleColumns.job && <TableHeader>Job</TableHeader>}
                                {visibleColumns.nationality && <TableHeader>Nationality</TableHeader>}
                                {visibleColumns.passportNumber && <TableHeader>Passport Number</TableHeader>}
                                {visibleColumns.cardType && <TableHeader>Card Type</TableHeader>}
                                {visibleColumns.cardNumber && <TableHeader>Card Number</TableHeader>}
                                {visibleColumns.cardExpiry && <TableHeader>Expiry Date</TableHeader>}
                                {visibleColumns.passportIssueDate && <TableHeader>Passport Issue</TableHeader>}
                                {visibleColumns.passportExpireDate && <TableHeader>Passport Expire</TableHeader>}
                                {visibleColumns.email && <TableHeader>Email</TableHeader>}
                                {visibleColumns.joiningDate && <TableHeader>Joining Date</TableHeader>}
                                {visibleColumns.years && <TableHeader>Years</TableHeader>}
                                {visibleColumns.daysLeft && <TableHeader>Days Left</TableHeader>}
                                {visibleColumns.status && <TableHeader>Status</TableHeader>}
                                <TableHeader><span className="sr-only">Actions</span></TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {employeesToShow.map((emp, idx) => {
                                const edits = editedRows[emp.staffNo] || {};
                                const hasChanges = Object.keys(edits).length > 0;
                                const isEditingName = editingCell?.staffNo === emp.staffNo && editingCell?.field === 'name';
                                const isEditingJob = editingCell?.staffNo === emp.staffNo && editingCell?.field === 'job';
                                const isEditingNationality = editingCell?.staffNo === emp.staffNo && editingCell?.field === 'nationality';
                                const isEditingDate = editingCell?.staffNo === emp.staffNo && editingCell?.field === 'cardExpiry';

                                return (
                                    <TableRow key={idx} className={hasChanges ? 'bg-yellow-50' : ''}>
                                        <TableCell><CellNumber value={idx + 1} /></TableCell>

                                        {visibleColumns.staffNo && <TableCell><CellId>{emp.staffNo}</CellId></TableCell>}

                                        {visibleColumns.name && (
                                            <TableCell onDoubleClick={() => handleCellDoubleClick(emp.staffNo, 'name')}>
                                                {isEditingName ? (
                                                    <input type="text" defaultValue={edits.name !== undefined ? edits.name : emp.name} onChange={(e) => handleFieldChange(emp.staffNo, 'name', e.target.value)} onBlur={handleCellBlur} autoFocus className="ds-input ds-input--sm w-full" />
                                                ) : <CellText>{formatName(edits.name !== undefined ? edits.name : emp.name)}</CellText>}
                                            </TableCell>
                                        )}

                                        {visibleColumns.job && (
                                            <TableCell onDoubleClick={() => handleCellDoubleClick(emp.staffNo, 'job')}>
                                                {isEditingJob ? (
                                                    <input type="text" defaultValue={edits.job !== undefined ? edits.job : emp.job} onChange={(e) => handleFieldChange(emp.staffNo, 'job', e.target.value)} onBlur={handleCellBlur} autoFocus className="ds-input ds-input--sm w-full" />
                                                ) : <CellText>{formatName(edits.job !== undefined ? edits.job : emp.job)}</CellText>}
                                            </TableCell>
                                        )}

                                        {visibleColumns.nationality && (
                                            <TableCell onDoubleClick={() => handleCellDoubleClick(emp.staffNo, 'nationality')}>
                                                {isEditingNationality ? (
                                                    <input type="text" defaultValue={edits.nationality !== undefined ? edits.nationality : emp.nationality} onChange={(e) => handleFieldChange(emp.staffNo, 'nationality', e.target.value)} onBlur={handleCellBlur} autoFocus className="ds-input ds-input--sm w-full" />
                                                ) : <CellText>{formatName(edits.nationality !== undefined ? edits.nationality : emp.nationality)}</CellText>}
                                            </TableCell>
                                        )}

                                        {visibleColumns.passportNumber && <TableCell><CellId>{emp.passportNumber}</CellId></TableCell>}
                                        {visibleColumns.cardType && <TableCell><CellTag>{emp.cardType}</CellTag></TableCell>}
                                        {visibleColumns.cardNumber && <TableCell><CellId>{emp.cardNumber}</CellId></TableCell>}

                                        {visibleColumns.cardExpiry && (
                                            <TableCell onDoubleClick={() => handleCellDoubleClick(emp.staffNo, 'cardExpiry')}>
                                                {isEditingDate ? (
                                                    <input type="text" defaultValue={(edits.cardExpiry !== undefined ? edits.cardExpiry : emp.cardExpiry)?.toLocaleDateString('en-GB') || ''} onChange={(e) => handleDateChange(emp.staffNo, e.target.value)} onBlur={(e) => { formatAndSetDate(emp.staffNo, e.target); handleCellBlur(); }} autoFocus className="ds-input ds-input--sm w-32" />
                                                ) : <CellDate value={(edits.cardExpiry !== undefined ? edits.cardExpiry : emp.cardExpiry)?.toLocaleDateString('en-GB')} />}
                                            </TableCell>
                                        )}

                                        {visibleColumns.passportIssueDate && <TableCell><CellDate value={emp.passportIssueDate ? String(emp.passportIssueDate) : ''} /></TableCell>}
                                        {visibleColumns.passportExpireDate && <TableCell><CellDate value={emp.passportExpireDate ? String(emp.passportExpireDate) : ''} /></TableCell>}
                                        {visibleColumns.email && <TableCell><CellText>{emp.email}</CellText></TableCell>}
                                        {visibleColumns.joiningDate && <TableCell><CellDate value={emp.joiningDate ? String(emp.joiningDate) : ''} /></TableCell>}
                                        {visibleColumns.years && <TableCell><CellNumber value={emp.years || ''} /></TableCell>}

                                        {visibleColumns.daysLeft && <TableCell><CellNumber value={emp.daysUntilExpiry} /></TableCell>}

                                        {visibleColumns.status && (
                                            <TableCell>
                                                <CellStatus
                                                    status={getStatusText(emp.daysUntilExpiry)}
                                                    label={getStatusText(emp.daysUntilExpiry)}
                                                    tone={
                                                        emp.daysUntilExpiry == null ? 'neutral' :
                                                            emp.daysUntilExpiry <= 0 ? 'danger' :
                                                                emp.daysUntilExpiry <= 7 ? 'danger' :
                                                                    emp.daysUntilExpiry <= 30 ? 'warning' : 'success'
                                                    }
                                                />
                                            </TableCell>
                                        )}

                                        <TableCell>
                                            {canDeleteStaff && (
                                                <IconButton
                                                    icon={ICONS.trash}
                                                    label="Delete employee"
                                                    size="sm"
                                                    tone="danger"
                                                    onClick={() => handleDeleteEmployee(emp.staffNo)}
                                                />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                )}
            </div>
        </Layout>
    );
}

export default Staff;
